import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

export default function VoicePhotoDialog({ request, sessionId, context, onClose }) {
    const alive = useRef(true);
    const pending = useRef(false);
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [saved, setSaved] = useState(false);
    useEffect(() => {
        alive.current = true;
        request(`/sessions/${sessionId}/photos`).then((result) => {
            if (!alive.current) return;
            if (result.context_version !== context.context_version) throw new Error('Photo context changed; reopen the photo list.');
            setPhotos(result.photos);
        }).catch((failure) => { if (alive.current) setError(failure.message); })
            .finally(() => { if (alive.current) setBusy(false); });
        return () => { alive.current = false; };
    }, [request, sessionId, context]);
    const selectPhoto = async (key) => {
        if (pending.current) return;
        pending.current = true; setBusy(true); setError(''); setSelected(null); setSaved(false);
        try {
            const result = await request(`/sessions/${sessionId}/photos/selection`, { method: 'POST', body: {
                photo_key: key, context_version: context.context_version } });
            if (!alive.current) return;
            if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(result.image?.content_type)) throw new Error('Photo preview unavailable.');
            setSelected({ ...result, proposed: result.caption?.caption || '', reason: '', requestId: crypto.randomUUID() });
        } catch (failure) { if (alive.current) setError(failure.message); }
        finally { if (alive.current) { setBusy(false); pending.current = false; } }
    };
    const correctCaption = async () => {
        if (pending.current || !selected?.caption || !selected.proposed.trim() || !selected.reason.trim()) return;
        pending.current = true; setBusy(true); setError(''); setSaved(false);
        try {
            const result = await request(`/sessions/${sessionId}/photos/${selected.selected_photo.photo_key}/caption`, { method: 'PUT', body: {
                request_id: selected.requestId, selection_id: selected.selected_photo.selection_id, context_version: context.context_version,
                expected_version: selected.caption.version, image_hash: selected.selected_photo.image_hash, caption: selected.proposed, reason: selected.reason } });
            if (!alive.current) return;
            setSelected((current) => ({ ...current, caption: result.caption, proposed: result.caption.caption, reason: '', requestId: crypto.randomUUID() }));
            setSaved(true);
        } catch (failure) { if (alive.current) setError(failure.message); }
        finally { if (alive.current) { setBusy(false); pending.current = false; } }
    };
    return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>Photo Captions</DialogTitle>
        <DialogContent><Stack spacing={1.5} sx={{ pt: 1, overflowWrap: 'anywhere' }}>
            <Typography variant="body2">{context.project_reference} / {context.report_instance} / visit {context.visit_id}</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">Caption correction saved. Review required.</Alert>}
            {!!photos.length && <TextField select label="Select photo" value={selected?.selected_photo.photo_key || ''} disabled={busy || !!context.read_only}
                onChange={(event) => selectPhoto(event.target.value)}>
                {photos.map((photo) => <MenuItem key={photo.key} value={photo.key} sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{photo.label}</MenuItem>)}
            </TextField>}
            {busy && <CircularProgress size={20} aria-label="Loading photo captions" />}
            {!busy && !photos.length && !error && <Typography>No photos in this reporting period.</Typography>}
            {selected && <>
                <Box component="img" alt={selected.selected_photo.label} src={`data:${selected.image.content_type};base64,${selected.image.data_base64}`}
                    sx={{ display: 'block', width: '100%', height: { xs: 220, sm: 300 }, objectFit: 'contain', bgcolor: 'grey.100' }}
                    onError={() => setError('Photo preview could not be rendered.')} />
                <Alert severity="info">Selected for caption. Review required.</Alert>
                {selected.caption ? <>
                    <Typography variant="subtitle2">Current Caption / v{selected.caption.version}</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.caption.caption}</Typography>
                    <TextField label="Proposed caption" multiline minRows={3} maxRows={8} value={selected.proposed} disabled={busy}
                        inputProps={{ maxLength: 2000 }} onChange={(event) => {
                            setSaved(false); setSelected((current) => ({ ...current, proposed: event.target.value, requestId: crypto.randomUUID() }));
                        }} />
                    <TextField label="Caption correction reason" value={selected.reason} disabled={busy} inputProps={{ maxLength: 1000 }} onChange={(event) => {
                        setSaved(false); setSelected((current) => ({ ...current, reason: event.target.value, requestId: crypto.randomUUID() }));
                    }} />
                </> : <Typography>No saved caption.</Typography>}
            </>}
        </Stack></DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
            {selected?.caption && <Button startIcon={<SaveIcon />} onClick={correctCaption}
                disabled={busy || !selected.proposed.trim() || !selected.reason.trim() || !!context.read_only}>Save caption correction</Button>}
            <Button onClick={onClose} disabled={pending.current}>Close</Button>
        </DialogActions>
    </Dialog>;
}